import { Capacitor } from "@capacitor/core";
import { AdMob, AdmobConsentStatus } from "@capacitor-community/admob";

const TEST_INTERSTITIAL_ID = "ca-app-pub-3940256099942544/4411468910";
const LOSS_COUNT_KEY = "bwwk-losses-before-interstitial";
const LOSS_THRESHOLD = 5;

type BuildEnv = {
  VITE_ADMOB_LIVE?: string;
  VITE_ADMOB_INTERSTITIAL_ID?: string;
};

function buildEnv(): BuildEnv {
  return (import.meta as ImportMeta & { env?: BuildEnv }).env ?? {};
}

export function isNativeIos(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
}

export function nativeBannerConfig(): { adUnitId: string; isTesting: boolean } {
  const live = buildEnv().VITE_ADMOB_LIVE === "true";
  return {
    adUnitId: live
      ? "ca-app-pub-1063268836085273/3783379985"
      : "ca-app-pub-3940256099942544/2934735716",
    isTesting: !live,
  };
}

function nativeInterstitialConfig(): { adUnitId: string; isTesting: boolean } | null {
  const env = buildEnv();
  const live = env.VITE_ADMOB_LIVE === "true";
  const adUnitId = live ? env.VITE_ADMOB_INTERSTITIAL_ID?.trim() : TEST_INTERSTITIAL_ID;
  if (!adUnitId) return null;
  return { adUnitId, isTesting: !live };
}

let readyPromise: Promise<boolean> | undefined;

/** Initializes AdMob once and completes ATT/UMP consent before an ad request. */
export function ensureAdMobReady(): Promise<boolean> {
  if (!isNativeIos()) return Promise.resolve(false);
  if (readyPromise) return readyPromise;

  readyPromise = (async () => {
    const trackingInfo = await AdMob.trackingAuthorizationStatus();
    if (trackingInfo.status === "notDetermined") {
      await AdMob.requestTrackingAuthorization();
    }

    await AdMob.initialize();
    let consentInfo = await AdMob.requestConsentInfo();
    if (
      consentInfo.isConsentFormAvailable &&
      consentInfo.status === AdmobConsentStatus.REQUIRED
    ) {
      consentInfo = await AdMob.showConsentForm();
    }
    return Boolean(consentInfo.canRequestAds);
  })().catch((error) => {
    readyPromise = undefined;
    console.warn("AdMob could not be initialized", error);
    return false;
  });

  return readyPromise;
}

async function showLossInterstitial(): Promise<void> {
  const config = nativeInterstitialConfig();
  if (!config) {
    console.warn("AdMob interstitial skipped: VITE_ADMOB_INTERSTITIAL_ID is not configured");
    return;
  }
  if (!(await ensureAdMobReady())) return;

  try {
    await AdMob.prepareInterstitial({
      adId: config.adUnitId,
      isTesting: config.isTesting,
    });
    await AdMob.showInterstitial({ adId: config.adUnitId });
  } catch (error) {
    console.warn("AdMob interstitial could not be shown", error);
  }
}

/** Counts local-player losses and shows one interstitial at every fifth loss. */
export function recordLossAndMaybeShowInterstitial(): void {
  if (!isNativeIos() || typeof window === "undefined") return;

  let losses = 0;
  try {
    losses = Number.parseInt(window.localStorage.getItem(LOSS_COUNT_KEY) ?? "0", 10);
    if (!Number.isFinite(losses) || losses < 0) losses = 0;
  } catch {
    losses = 0;
  }

  const nextLosses = losses + 1;
  if (nextLosses < LOSS_THRESHOLD) {
    try {
      window.localStorage.setItem(LOSS_COUNT_KEY, String(nextLosses));
    } catch {
      // Ads remain optional when storage is unavailable.
    }
    return;
  }

  try {
    window.localStorage.setItem(LOSS_COUNT_KEY, "0");
  } catch {
    // The current loss still triggers the ad even if the counter cannot persist.
  }
  void showLossInterstitial();
}
